import { DateTime as DT } from 'luxon';
import type { TaskType } from '@econtractor/types';
import {
    dueDatesFromTask,
    normalizeDueDateValues,
} from '../../shared/helpers/taskDueDateHelpers';
import type { NormalizedTaskDueDates } from './applyCalendarEventDrop';

/**
 * Resize. All-day `end` is exclusive; a timed `end` is the real end.
 * Two or more inclusive days turn multiDay on and withTime off (from–to dates only).
 * A later shrink to one day leaves withTime off.
 * A timed resize that stays on one day is ignored — hours stay as they were.
 */
export const applyCalendarEventResize = (
    task: TaskType,
    start: Date,
    end: Date | null,
    allDay: boolean,
): NormalizedTaskDueDates | null => {
    const current = dueDatesFromTask(task);
    const startDt = DT.fromJSDate(start, { zone: 'local' });
    if (!startDt.isValid) return null;

    const endDt = end
        ? DT.fromJSDate(end, { zone: 'local' })
        : startDt.plus({ days: 1 });
    if (!endDt.isValid) return null;

    const startDay = startDt.startOf('day');
    let lastDay = endDt.startOf('day');
    if (allDay) {
        lastDay = lastDay.minus({ days: 1 });
    } else if (
        endDt.hour === 0
        && endDt.minute === 0
        && endDt.second === 0
        && endDt.millisecond === 0
    ) {
        lastDay = lastDay.minus({ days: 1 });
    }
    if (lastDay < startDay) return null;

    const multiDay = lastDay.toMillis() !== startDay.toMillis();
    if (!allDay && !multiDay) return null;

    return normalizeDueDateValues({
        ...current,
        multiDay,
        withTime: multiDay ? false : current.withTime,
        dateStart: startDay.toMillis(),
        dateEnd: lastDay.toMillis(),
    });
};
